import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ErrorNotificationContextValue {
  showError: (message: string, title?: string) => void;
}

const ErrorNotificationContext = createContext<ErrorNotificationContextValue>({
  showError: () => {},
});

export const useErrorNotification = () => useContext(ErrorNotificationContext);

/**
 * Drop-in replacement for `const [error, setError] = useState('')` or `useState<string | null>(null)`.
 * Keeps local error state (for field validation) AND shows a modal when error is set.
 * Just swap the import and delete the inline <Alert>.
 */
export const useErrorModal = (): [string, (msg: string | null) => void] => {
  const { showError } = useErrorNotification();
  const [error, _setError] = useState('');

  const setError = useCallback((msg: string | null) => {
    _setError(msg || '');
    if (msg) showError(msg);
  }, [showError]);

  return [error, setError];
};

export const ErrorNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('Error');

  const showError = useCallback((msg: string, t?: string) => {
    setMessage(msg);
    setTitle(t || 'Error');
    setOpen(true);
  }, []);

  const handleClose = () => setOpen(false);

  return (
    <ErrorNotificationContext.Provider value={{ showError }}>
      {children}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="h6" component="span">{title}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorNotificationContext.Provider>
  );
};
